import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Sparkles, XCircle, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_STYLES = {
  confirmed: "text-emerald-700",
  pending: "text-amber-700",
  cancelled: "text-red-600",
};

export default function MyBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(null); // { booking, preview }
  const [cancelling, setCancelling] = useState(false);

  const load = () =>
    api.get("/bookings/mine")
      .then(({ data }) => setBookings(data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openCancel = async (b) => {
    try {
      const { data } = await api.get(`/bookings/${b.booking_id}/cancel-preview`);
      setCancelDialog({ booking: b, preview: data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load cancellation policy");
    }
  };

  const confirmCancel = async () => {
    if (!cancelDialog) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/bookings/${cancelDialog.booking.booking_id}/cancel`, {});
      if (data.refund_amount > 0) {
        toast.success(`Booking cancelled — ₹${data.refund_amount.toLocaleString("en-IN")} credited to your wallet`);
      } else {
        toast.success("Booking cancelled — no refund per policy");
      }
      setCancelDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    } finally { setCancelling(false); }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="hw-label text-blue-700">Bookings</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">
          {user?.role === "hotel_partner" ? "Bookings at your hotel" : "Your bookings"}
        </h1>

        {loading ? (
          <div className="hw-card p-10 mt-8 text-center" data-testid="bookings-loading">
            <span className="hw-pulse-dot" />
            <p className="text-slate-500 mt-3">Loading your bookings…</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="hw-card p-10 mt-8 text-center" data-testid="bookings-empty">
            <p className="text-slate-600">No bookings yet.</p>
          </div>
        ) : (
          <div className="space-y-3 mt-8" data-testid="bookings-list">
            {bookings.map((b) => (
              <div key={b.booking_id} className="hw-card p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`booking-${b.booking_id}`}>
                <div>
                  <h3 className="hw-head font-bold text-lg text-slate-900">{b.hotel_name}</h3>
                  <div className="text-sm text-slate-500">{b.checkin} → {b.checkout} · {b.rooms} rooms</div>
                  <div className="text-xs hw-mono text-slate-400 mt-1">UPI: {b.upi_txn_id || "—"}</div>
                  {b.coupon_code && (
                    <div className="text-xs text-emerald-700 mt-1"><Sparkles className="w-3 h-3 inline" /> {b.coupon_code} · saved ₹{(b.discount || 0).toLocaleString("en-IN")}</div>
                  )}
                  {b.status === "cancelled" && b.refund_amount > 0 && (
                    <div className="text-xs text-emerald-700 mt-1 font-semibold" data-testid={`refund-${b.booking_id}`}>
                      Refund ₹{b.refund_amount.toLocaleString("en-IN")} credited to wallet
                    </div>
                  )}
                  {b.status === "cancelled" && b.refund_amount === 0 && (
                    <div className="text-xs text-slate-500 mt-1">No refund per {b.cancellation_policy} policy</div>
                  )}
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <div className={`hw-label uppercase ${STATUS_STYLES[b.status] || "text-slate-600"}`}>{b.status}</div>
                  <div className="hw-head text-2xl font-black text-slate-900">₹{b.price?.toLocaleString("en-IN")}</div>
                  {user?.role === "customer" && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button asChild variant="outline" size="sm" className="rounded-sm border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white h-8" data-testid={`invoice-btn-${b.booking_id}`}>
                        <Link to={`/invoice/${b.booking_id}`}><FileText className="w-3 h-3 mr-1.5" /> Invoice</Link>
                      </Button>
                      {b.status !== "cancelled" && (
                        <Button variant="outline" size="sm" onClick={() => openCancel(b)}
                          className="rounded-sm border-red-500 text-red-600 hover:bg-red-500 hover:text-white h-8" data-testid={`cancel-btn-${b.booking_id}`}>
                          <XCircle className="w-3 h-3 mr-1.5" /> Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cancel confirmation dialog with live refund calculator */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => !o && setCancelDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="hw-head text-xl">Cancel booking?</DialogTitle>
          </DialogHeader>
          {cancelDialog?.preview && (
            <div className="space-y-4">
              <div className="hw-card border-l-4 border-l-emerald-500 bg-emerald-50 p-4">
                <div className="hw-label text-emerald-700 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> {cancelDialog.preview.policy.label} cancellation policy
                </div>
                <div className="text-sm text-slate-700 mt-1">{cancelDialog.preview.policy.summary}</div>
              </div>

              <div className="hw-card p-4">
                <div className="hw-label text-slate-500 mb-2">Live refund calculator</div>
                <div className="space-y-2">
                  {cancelDialog.preview.timeline.map((m, i) => (
                    <div key={i} className={`flex items-center justify-between text-sm py-1 ${i === 0 ? "font-bold" : "text-slate-600"}`} data-testid={`timeline-row-${i}`}>
                      <span>{m.label}</span>
                      <span className={m.refund_percent > 0 ? "text-emerald-700" : "text-red-600"}>
                        ₹{m.refund_amount.toLocaleString("en-IN")} ({m.refund_percent}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-center hw-card border-2 border-emerald-500 bg-emerald-50 p-4" data-testid="refund-summary">
                <div className="hw-label text-slate-500">You will receive</div>
                <div className="hw-head text-3xl font-black text-emerald-800 mt-1">
                  ₹{cancelDialog.preview.timeline[0].refund_amount.toLocaleString("en-IN")}
                </div>
                <div className="text-xs text-slate-600 mt-1">Credited instantly to your SwaanStay wallet — usable on your next booking</div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelDialog(null)} disabled={cancelling}
              className="rounded-sm" data-testid="cancel-dialog-back">Keep booking</Button>
            <Button onClick={confirmCancel} disabled={cancelling}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="cancel-dialog-confirm">
              {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
              Confirm cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
